<template>
  <div class="album-wrapper">
    <div class="stars-container">
      <div v-for="n in 50" :key="'s'+n" class="star" :style="generateStarStyle()"></div>
    </div>

    <div v-show="isLoading" class="loader-container">
      <div ref="lottieLoader" class="lottie-animation"></div>
      <p class="loader-text">Guardando tus recuerdos...</p>
    </div>

    <div ref="bienvenidaRef" id="album" class="glass-content">
      <div class="card-header">
        <div class="icon-orb">
          <img src="@/assets/camara.png" alt="Cámara" class="camara-img">
        </div>
        <h4 class="titulo-dorado">Álbum de Fotos</h4>
        <p class="subtitulo-blanco">El día del evento ayudame compartiendo los mejores momentos.</p>
      </div>

      <label for="inputFile" class="modern-upload-zone" :class="{ 'active-files': selectedFiles.length > 0 }">
        <div class="upload-inner">
          <i class="fas fa-camera-retro"></i>
          <span class="main-text">{{ selectedFiles.length > 0 ? 'Añadir más fotos' : 'Seleccionar imágenes' }}</span>
          <span class="limit-text">Máximo 10 archivos</span>
        </div>
      </label>

      <input 
        id="inputFile"
        ref="fileInput"
        type="file"
        class="hidden-input"
        accept="image/*"
        multiple
        @change="validateFiles"
      >

      <p v-if="errorMessage" class="error-msg">{{ errorMessage }}</p>

      <transition-group name="list" tag="div" v-if="previewImages.length > 0" class="modern-preview-grid">
        <div v-for="(src, index) in previewImages" :key="src" class="preview-card">
          <img :src="src" alt="Preview" class="preview-img">
          <button class="btn-delete" @click="removeImage(index)">
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>
      </transition-group>

      <div class="actions" v-if="selectedFiles.length > 0">
        <button class="btn-gold" @click="confirmUpload">
          <i class="fas fa-cloud-upload-alt"></i> SUBIR FOTOS
        </button>
      </div>

      <p v-if="photoUploaded" class="success-msg">
        <i class="fas fa-check-circle"></i> ¡Gracias por compartir tus fotos!
      </p>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from 'vue';
import axios from 'axios';
import baseUrl from '../api';
import Swal from 'sweetalert2';
import lottie from 'lottie-web';
import heartAnimation from '../assets/heart.json';
import gsap from 'gsap';

export default {
  name: 'HashtagEtiqueta',
  props: {
    idInvitado: { type: Number, required: true },
    nombre: { type: String, default: '' },
    idFestejado: { type: Number, default: null }
  },
  setup(props) {
    const errorMessage = ref('');
    const fileInput = ref(null);
    const selectedFiles = ref([]);
    const previewImages = ref([]);
    const photoUploaded = ref(false);
    const isLoading = ref(false);
    const lottieLoader = ref(null);
    const bienvenidaRef = ref(null);
    const MAX_FILES = 10;

    const generateStarStyle = () => ({
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      width: `${Math.random() * 2 + 1}px`,
      height: `${Math.random() * 2 + 1}px`,
      animationDuration: `${Math.random() * 3 + 2}s`
    });

    onMounted(() => {
      gsap.from(".glass-content", { opacity: 0, scale: 0.95, duration: 1.5, ease: "expo.out" });
    });

    watch(isLoading, (newVal) => {
      if (newVal && lottieLoader.value) {
        lottie.loadAnimation({
          container: lottieLoader.value,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: heartAnimation
        });
      }
    });

    const validateFiles = (event) => {
      const files = Array.from(event.target.files);
      if (files.length + selectedFiles.value.length > MAX_FILES) {
        Swal.fire('Atención', `Solo puedes subir hasta ${MAX_FILES} fotos`, 'info');
        return;
      }
      const newPreviews = files.map(file => URL.createObjectURL(file));
      selectedFiles.value = [...selectedFiles.value, ...files];
      previewImages.value = [...previewImages.value, ...newPreviews];
      errorMessage.value = '';
    };

    const removeImage = (index) => {
      selectedFiles.value.splice(index, 1);
      previewImages.value.splice(index, 1);
    };

    const confirmUpload = async () => {
      isLoading.value = true;
      const formData = new FormData();
      selectedFiles.value.forEach(file => formData.append('fotos', file));
      const params = [props.nombre, props.idFestejado, props.idInvitado].filter(p => p).join('/');
      const url = `${baseUrl}/subirfotos/${params}`;

      try {
        await axios.post(url, formData);
        photoUploaded.value = true;
        selectedFiles.value = [];
        previewImages.value = [];
        Swal.fire({
          title: '¡Éxito!',
          text: 'Tus fotos se han guardado correctamente.',
          icon: 'success',
          confirmButtonColor: '#a48250'
        });
      } catch (e) {
        Swal.fire('Error', 'Hubo un problema al subir las fotos.', 'error');
      } finally {
        isLoading.value = false;
      }
    };

    return { 
      errorMessage, fileInput, confirmUpload, validateFiles, 
      photoUploaded, bienvenidaRef, previewImages, removeImage, 
      isLoading, lottieLoader, generateStarStyle, selectedFiles 
    };
  }
};
</script>

<style scoped>
.album-wrapper {
  position: relative;
  min-height: 40vh;
  padding: 40px 20px;
  background: linear-gradient(to bottom, #000000 0%, #08062d 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

/* --- ESTRELLAS --- */
.stars-container {
  position: absolute;
  top: 0; left: 0; width: 100%; height: 100%;
}
.star {
  position: absolute;
  background: white;
  border-radius: 50%;
  animation: twinkle linear infinite;
}
@keyframes twinkle {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 0.8; transform: scale(1.2); }
}

/* --- CONTENEDOR GLASS --- */
.glass-content {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 450px;
  background: rgba(255, 255, 255, 0.03); /* Casi invisible */
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 40px;
  padding: 40px 20px;
  text-align: center;
}

.icon-orb {
  width: 70px;
  height: 70px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 20px;
  border: 1px solid rgba(164, 130, 80, 0.5);
}

.camara-img {
  width: 35px;
  filter: brightness(0) invert(1); /* Pone la cámara en blanco */
}

.titulo-dorado {
  font-family: 'Playfair Display', serif;
  font-size: 32px;
  color: #a48250; /* Dorado */
  margin: 0;
  letter-spacing: 1px;
}

.subtitulo-blanco {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 8px;
}

/* --- ZONA DE CARGA --- */
.modern-upload-zone {
  display: block;
  margin-top: 30px;
  padding: 30px;
  border: 1px dashed rgba(164, 130, 80, 0.5);
  border-radius: 20px;
  background: rgba(164, 130, 80, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
}

.modern-upload-zone:hover {
  background: rgba(164, 130, 80, 0.15);
  border-color: #a48250;
}

.modern-upload-zone i {
  font-size: 30px;
  color: #a48250;
  margin-bottom: 10px;
}

.main-text {
  display: block;
  color: #ffffff;
  font-weight: 500;
  font-size: 16px;
}

.limit-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
}

.hidden-input { display: none; }

/* --- PREVIEW GRID --- */
.modern-preview-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 25px;
}

.preview-card {
  position: relative;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.btn-delete {
  position: absolute;
  top: 5px; right: 5px;
  background: rgba(255, 255, 255, 0.9);
  color: #000;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 11px;
  cursor: pointer;
}

/* --- BOTÓN DORADO --- */
.btn-gold {
  width: 100%;
  margin-top: 25px;
  padding: 16px;
  border-radius: 50px;
  border: none;
  background: linear-gradient(45deg, #a48250, #d4af37);
  color: #000;
  font-weight: 800;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 10px 20px rgba(164, 130, 80, 0.3);
}

.btn-gold:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 30px rgba(164, 130, 80, 0.5);
}

.success-msg {
  margin-top: 20px;
  color: #a48250;
  font-size: 14px;
}

.error-msg {
  color: #ff4d4d;
  font-size: 13px;
  margin-top: 10px;
}

/* --- LOADER --- */
.loader-container {
  position: fixed; inset: 0; background: rgba(0,0,0,0.9); z-index: 10000;
  display: flex; flex-direction: column; justify-content: center; align-items: center;
}
.lottie-animation { width: 150px; height: 150px; }
.loader-text { color: white; margin-top: 20px; font-family: 'Dancing Script', cursive; font-size: 20px; }
</style>